import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BookOpen, Briefcase, Calculator, FileText, FolderOpen, HelpCircle, ListTodo, LucideIcon,
  MessageSquare, MoreHorizontal, Plus, Settings, Sparkles, Sun, Users,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { isTwa } from '../../utils/platform';
import { CreateMenu } from './CreateMenu';
import { Sheet } from './Sheet';
import { WHATSAPP_URL, WhatsAppIcon } from '../../pages/landing/data';

/** §2.2 — Today / Cases / ＋ / Drafts / More. Same words as the sidebar. */
const TABS = [
  { to: '/app', icon: Sun, label: 'Today', end: true, tour: 'nav-today' },
  { to: '/app/cases', icon: Briefcase, label: 'Cases', end: false, tour: 'nav-cases' },
] as const;

const TABS_AFTER = [
  { to: '/app/drafts', icon: FileText, label: 'Drafts', end: false, tour: 'nav-drafts' },
] as const;

interface MoreItem {
  label: string;
  icon: LucideIcon;
  to: string;
  track: string;
  tour?: string;
}

const MORE_ITEMS: MoreItem[] = [
  { label: 'Research', icon: BookOpen, to: '/app/research', track: 'more-research', tour: 'nav-research' },
  { label: 'Documents', icon: FolderOpen, to: '/app/documents', track: 'more-documents', tour: 'nav-documents' },
  { label: 'Clients', icon: Users, to: '/app/clients', track: 'more-clients', tour: 'nav-clients' },
  { label: 'All tasks', icon: ListTodo, to: '/app/tasks', track: 'more-tasks' },
  { label: 'Settings', icon: Settings, to: '/app/settings', track: 'more-settings' },
  { label: 'Send feedback', icon: MessageSquare, to: '/feedback', track: 'more-feedback' },
  { label: 'How Urava works', icon: HelpCircle, to: '/user-guide', track: 'more-guide' },
];

const ROW_CLS =
  'w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-legal-50 text-left text-sm font-medium text-legal-800 transition-colors press';

function tabCls({ isActive }: { isActive: boolean }) {
  return `relative flex-1 flex flex-col items-center justify-center py-2.5 min-h-[44px] text-xs transition-all press ${
    isActive ? 'text-accent' : 'text-legal-500'
  }`;
}

function TabLink({ tab }: { tab: { to: string; icon: LucideIcon; label: string; end: boolean; tour: string } }) {
  return (
    <NavLink key={tab.to} to={tab.to} end={tab.end} data-tour={tab.tour} data-track={tab.tour} className={tabCls}>
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={`absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full transition-colors ${
              isActive ? 'bg-accent' : 'bg-transparent'
            }`}
          />
          <tab.icon className={`h-5 w-5 mb-0.5 ${isActive ? 'text-accent' : 'text-legal-400'}`} />
          <span>{tab.label}</span>
        </>
      )}
    </NavLink>
  );
}

export function BottomNav() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [sheet, setSheet] = useState<null | 'create' | 'more'>(null);

  const isFreeTier = !user?.subscription_tier || user.subscription_tier === 'free';

  const go = (to: string) => {
    setSheet(null);
    navigate(to);
  };

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-legal-200 flex">
        {TABS.map((tab) => (
          <TabLink key={tab.to} tab={tab} />
        ))}

        {/* Raised center ＋ — same create menu as desktop */}
        <button
          type="button"
          onClick={() => setSheet('create')}
          data-tour="new-button-mobile"
          data-track="nav-new-mobile"
          aria-label="Create new"
          aria-haspopup="menu"
          aria-expanded={sheet === 'create'}
          className="relative flex-1 flex flex-col items-center justify-end pb-1.5 min-h-[44px] text-xs text-legal-500 press"
        >
          <span className="absolute -top-4 left-1/2 -translate-x-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-legal-900 shadow-elevation-2 border-4 border-white">
            <Plus className="h-6 w-6" />
          </span>
          <span className="mt-7">New</span>
        </button>

        {TABS_AFTER.map((tab) => (
          <TabLink key={tab.to} tab={tab} />
        ))}

        <button
          type="button"
          onClick={() => setSheet('more')}
          data-track="nav-more"
          aria-label="More"
          aria-haspopup="menu"
          aria-expanded={sheet === 'more'}
          className={tabCls({ isActive: false })}
        >
          <MoreHorizontal className="h-5 w-5 mb-0.5 text-legal-400" />
          <span>More</span>
        </button>
      </nav>

      {sheet === 'create' && <CreateMenu variant="sheet" onClose={() => setSheet(null)} />}

      {sheet === 'more' && (
        <Sheet title="More" onClose={() => setSheet(null)}>
          {MORE_ITEMS.map((item) => (
            <button
              key={item.track}
              type="button"
              data-track={item.track}
              data-tour={item.tour}
              onClick={() => go(item.to)}
              className={ROW_CLS}
            >
              <item.icon className="h-5 w-5 text-legal-500 shrink-0" />
              <span>{item.label}</span>
            </button>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-track="more-whatsapp"
            onClick={() => setSheet(null)}
            className={ROW_CLS}
          >
            <WhatsAppIcon className="h-5 w-5 text-[#25D366] shrink-0" />
            <span>Urava on WhatsApp</span>
          </a>
          {/* Edge-worker page — full page load in a new tab, not a SPA <Link> */}
          <a
            href="/tools"
            target="_blank"
            rel="noopener noreferrer"
            data-track="more-tools"
            onClick={() => setSheet(null)}
            className={ROW_CLS}
          >
            <Calculator className="h-5 w-5 text-legal-500 shrink-0" />
            <span>Free legal calculators</span>
          </a>
          {isFreeTier && !isTwa() && (
            <button
              type="button"
              data-track="more-upgrade"
              onClick={() => go('/pricing')}
              className={ROW_CLS}
            >
              <Sparkles className="h-5 w-5 text-accent-700 shrink-0" />
              <span>Upgrade</span>
            </button>
          )}
        </Sheet>
      )}
    </>
  );
}
